/** Standard EAN code prefix for Belgium */
export var EAN_PREFIX = "54";
/**
 * Truncate EAN code by removing the standard prefix
 */
export var truncateEAN = function (eanCode) {
    return (eanCode === null || eanCode === void 0 ? void 0 : eanCode.startsWith(EAN_PREFIX))
        ? eanCode.replace(EAN_PREFIX, "")
        : eanCode || undefined;
};
/**
 * Prepend EAN prefix to a truncated EAN code
 */
export var prependEANPrefix = function (eanCode) {
    if (!eanCode)
        return undefined;
    return eanCode.startsWith(EAN_PREFIX) ? eanCode : "".concat(EAN_PREFIX).concat(eanCode);
};
/**
 * Predefined amenity name constants used throughout the application.
 * These follow the naming convention: rentio.default.<snake_case_field_name>
 */
export var AMENITY_NAMES = {
    // General
    costsComment: "rentio.default.costs_comment",
    // The habitable surface, which is what the Whise import writes its `Area`
    // value to. Distinct from buildingSurfaceArea, the surface of the whole
    // building.
    livingSurfaceArea: "rentio.default.living_surface_area",
    buildingFloor: "rentio.default.building_floor",
    buildingFloorCount: "rentio.default.building_floor_count",
    roomCountBedroom: "rentio.default.room_count_bedroom",
    roomCountBathroom: "rentio.default.room_count_bathroom",
    // Exterior
    buildingSurfaceArea: "rentio.default.building_surface_area",
    groundSurfaceArea: "rentio.default.ground_surface_area",
    hasGarden: "rentio.default.has_garden",
    gardenOrientation: "rentio.default.garden_orientation",
    hasTerrace: "rentio.default.has_terrace",
    terraceOrientation: "rentio.default.terrace_orientation",
    hasGardenShed: "rentio.default.has_garden_shed",
    hasPool: "rentio.default.has_pool",
    numberOfParkingsOutside: "rentio.default.number_of_parkings_outside",
    // Interior
    numberOfToilets: "rentio.default.number_of_toilets",
    numberOfShowerRooms: "rentio.default.number_of_shower_rooms",
    hasAttic: "rentio.default.has_attic",
    buildingHasStorage: "rentio.default.building_has_storage",
    isFurnished: "rentio.default.is_furnished",
    kitchenType: "rentio.default.kitchen_type",
    floorType: "rentio.default.floor_type",
    buildingHasHardMaterials: "rentio.default.building_has_hard_materials",
    hasOven: "rentio.default.has_oven",
    hasFridge: "rentio.default.has_fridge",
    hasDishwasher: "rentio.default.has_dishwasher",
    hasRangeHood: "rentio.default.has_range_hood",
    roomCountGarage: "rentio.default.room_count_garage",
    numberOfParkingsInside: "rentio.default.number_of_parkings_inside",
    // Comfort
    hasLift: "rentio.default.has_lift",
    hasAirConditioning: "rentio.default.has_air_conditioning",
    hasAlarm: "rentio.default.has_alarm",
    hasVideophone: "rentio.default.has_videophone",
    hasSmokeDetectors: "rentio.default.has_smoke_detectors",
    accessibleForDisabled: "rentio.default.accessible_for_disabled",
    // Energy
    hasCentralHeating: "rentio.default.has_central_heating",
    heatingType: "rentio.default.heating_type",
    heatingDistribution: "rentio.default.heating_distribution",
    hasDoubleGlazing: "rentio.default.has_double_glazing",
    glazingType: "rentio.default.glazing_type",
    windowFrameMaterial: "rentio.default.window_frame_material",
    hasInsulation: "rentio.default.has_insulation",
    hasSolarPanels: "rentio.default.has_solar_panels",
    isLowEnergy: "rentio.default.is_low_energy",
    isPassiveHouse: "rentio.default.is_passive_house",
    co2Emission: "rentio.default.co2_emission",
    // EPC. The label, the score, the expiry and the certificate number are
    // property columns; these two come from the EPB report and are amenities.
    eLevel: "rentio.default.e_level",
    kLevel: "rentio.default.k_level",
    // House rules
    petsAllowed: "rentio.default.pets_allowed",
    petsAllowedDetail: "rentio.default.pets_allowed_detail",
    smokingAllowed: "rentio.default.smoking_allowed",
    // Condition and surroundings
    condition: "rentio.default.condition",
    renovationYear: "rentio.default.renovation_year",
    environmentType: "rentio.default.environment_type",
    // Structural
    buildingAdjoined: "rentio.default.building_adjoined",
    frontWidth: "rentio.default.front_width",
    volume: "rentio.default.volume",
    roofType: "rentio.default.roof_type",
    roofMaterial: "rentio.default.roof_material",
    // Risk and certificates
    floodZoneGScore: "rentio.default.flood_zone_g_score",
    floodZonePScore: "rentio.default.flood_zone_p_score",
    conformityCertificate: "rentio.default.conformity_certificate",
    electricityCertificate: "rentio.default.electricity_certificate",
    asbestosCertificate: "rentio.default.asbestos_certificate",
    asbestosCertificateDate: "rentio.default.asbestos_certificate_date",
    // Media
    //
    // Stored like every other amenity and grouped under this name by api-next.
    // Until now nothing in the dashboard named them, so the only way one was ever
    // filled was a partner CRM pushing a property in through
    // createExternalProperty, and nobody at Rentio could add or correct one.
    virtualTourUrl: "rentio.default.virtual_tour_url",
    videoUrl: "rentio.default.video_url",
    model3dUrl: "rentio.default.model_3d_url",
    floorPlanUrl: "rentio.default.floor_plan_url",
    // What the office asks of a candidate, which the Rentmatch score reads.
    //
    // These are the CRM catalog's own fields rather than two of our own beside
    // them. A broker thinks of one number per question, and a partner CRM already
    // offers exactly these two, so a second private pair would have meant one
    // question with two answers and no rule for which wins.
    //
    // The trade, accepted knowingly (Jonas, 2026-09-29): a CRM that sends these
    // in an update overwrites what the broker set here, and one that sends them
    // empty clears it. Our own names were unreachable for a CRM and so could not
    // be overwritten, but they were also invisible in the API, which is why they
    // were never the better answer.
    minimumIncome: "rentio.default.minimum_income",
    maxOccupants: "rentio.default.max_occupants",
    // Description
    descriptionLong: "rentio.default.description_long",
};
/**
 * Get the raw value of an amenity by name
 */
export var getAmenityValue = function (amenities, name) {
    if (!amenities)
        return undefined;
    var amenity = amenities.find(function (a) { return a.name === name; });
    return amenity === null || amenity === void 0 ? void 0 : amenity.value;
};
/**
 * Get an amenity value as a boolean
 */
export var getAmenityBooleanValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined)
        return null;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string")
        return value === "true";
    return null;
};
/**
 * Get an amenity value as a number
 */
export var getAmenityNumberValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined || value === null)
        return null;
    if (typeof value === "number")
        return value;
    if (typeof value === "string") {
        var parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
};
/**
 * Get an amenity value as a string
 */
export var getAmenityStringValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined || value === null)
        return null;
    return String(value);
};
/**
 * Create an amenity item for upsert operation.
 * Handles deletion by setting value to null when the value is undefined/null
 * and there's an existing amenity.
 *
 * @param propertyAmenities - Existing property amenities
 * @param name - Amenity name
 * @param value - New value (null/undefined triggers deletion if amenity exists)
 * @param dataType - Data type of the amenity
 * @returns IUpsertPropertyAmenityItem or null if no action needed
 */
export var createAmenityItem = function (propertyAmenities, name, value, dataType) {
    var existingAmenity = propertyAmenities === null || propertyAmenities === void 0 ? void 0 : propertyAmenities.find(function (a) { return a.name === name; });
    // If value is undefined and no existing amenity, don't include
    if (value === undefined && !existingAmenity)
        return null;
    // If value is undefined but there's an existing amenity, send null to delete it
    if (value === undefined && existingAmenity) {
        return {
            id: existingAmenity.id,
            name: name,
            dataType: dataType,
            value: null,
        };
    }
    // If value is null (explicitly cleared) and there's an existing amenity, send null to delete
    if (value === null && existingAmenity) {
        return {
            id: existingAmenity.id,
            name: name,
            dataType: dataType,
            value: null,
        };
    }
    // If value is null/undefined and no existing amenity, don't include
    if (value === null || value === undefined)
        return null;
    return {
        id: existingAmenity === null || existingAmenity === void 0 ? void 0 : existingAmenity.id,
        name: name,
        dataType: dataType,
        value: value,
    };
};
/**
 * Determine the data type from a value
 */
export var getDataTypeFromValue = function (value) {
    if (typeof value === "boolean")
        return "boolean";
    if (typeof value === "number")
        return "number";
    return "string";
};
/**
 * Check if an amenity name is a predefined default amenity
 */
export var isPredefinedAmenity = function (name) {
    return name.startsWith("rentio.default.");
};
